import {
  Button,
  ButtonLink,
  CurrencyInput,
  Divider,
  Input,
  Label,
  Option,
  Select,
  SubtractIcon,
} from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '../../../Components/Shared/Dropdown';
import { DiscountAmountType } from '../../../helpers/invoices';
import { InvoicePreview } from '../widgets';

const RecurringFeesForm = ({
  control,
  setValue,
  clearErrors,
  feeCategoryOptions,
  setFeeCategoryOptions,
  watch,
  selectedBillingInterval,
  findSelectedOptionName,
  register,
  errors,
  recurringInvoiceTotal,
  calculatedProrateAmount,
  startDate,
  nextInvoices,
  setIsModalOpen,
}) => {
  const { t } = useTranslation();

  const {
    fields: recurringFields,
    append: addRecurringFee,
    remove: removeRecurringFee,
  } = useFieldArray({
    control,
    name: 'recurringFees',
  });

  const feeTypeOptions = useMemo(
    () => [
      { label: t('enrollments.fee'), value: 'Fee' },
      { label: t('enrollments.discount'), value: 'Discount' },
    ],
    [t]
  );

  const isCurrency = (index: number) => {
    const recurringFees = watch('recurringFees');
    return recurringFees?.[index]?.amountType === DiscountAmountType.CURRENCY;
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{t('enrollments.recurringFees')}</h2>
          <ButtonLink
            type="button"
            extraClasses="text-base"
            onClick={() =>
              addRecurringFee({
                type: feeTypeOptions[0].value,
                category: '',
                amount: 0,
                amountType: DiscountAmountType.CURRENCY,
              })
            }
            content={t('enrollments.addFeeDiscount')}
          />
        </div>
        <div className="text-base">{t('enrollments.recurringFeesNote')}</div>

        {recurringFields.map((field, index) => (
          <div key={field.id} className="grid grid-cols-12 gap-2">
            <div className="col-span-2">
              <Controller
                name={`recurringFees.${index}.type`}
                control={control}
                render={() => (
                  <Select
                    label={t('enrollments.feeType')}
                    options={feeTypeOptions}
                    required
                    onChange={(option: Option) => {
                      setValue(`recurringFees.${index}.type`, option.value);
                      clearErrors(`recurringFees.${index}.type`);
                    }}
                    defaultValue={feeTypeOptions[0]}
                  />
                )}
              />
            </div>
            <div className="col-span-4 flex flex-col gap-3">
              <Label>{t('enrollments.feeCategory')}</Label>
              <Controller
                name={`recurringFees.${index}.category`}
                control={control}
                render={() => (
                  <Dropdown
                    allowAdditions
                    clearable
                    search
                    placeholder={t('enrollments.fees.typePlaceholder')}
                    label={t('enrollments.feeCategory')}
                    options={feeCategoryOptions[watch(`recurringFees.${index}.type`)]}
                    required
                    onChange={(_e, data) => {
                      setValue(`recurringFees.${index}.category`, data.value);
                      clearErrors(`recurringFees.${index}.category`);
                    }}
                    onAddItem={(_e, data) => {
                      const newOption = {
                        text: data.value,
                        label: data.value,
                        value: data.value,
                      };
                      setFeeCategoryOptions({
                        ...feeCategoryOptions,
                        [watch(`recurringFees.${index}.type`)]: [
                          ...feeCategoryOptions[watch(`recurringFees.${index}.type`)],
                          newOption,
                        ],
                      });
                      setValue(`recurringFees.${index}.category`, data.value);
                      clearErrors(`recurringFees.${index}.category`);
                    }}
                    value={watch(`recurringFees.${index}.category`)}
                    translator={t}
                    isForm={false}
                  />
                )}
              />
            </div>
            <div className="col-span-2 flex flex-col gap-2">
              {watch(`recurringFees.${index}.amountType`) === DiscountAmountType.CURRENCY ? (
                <Controller
                  name={`recurringFees.${index}.amount`}
                  control={control}
                  render={() => (
                    <CurrencyInput
                      label={t('enrollments.amount')}
                      onChange={({ target }) => {
                        const value = target.value.replace(',', '');
                        setValue(`recurringFees.${index}.amount`, parseFloat(value));
                        clearErrors(`recurringFees.${index}.amount`);
                      }}
                      value={(() => {
                        const amount = watch(`recurringFees.${index}.amount`);
                        return amount && typeof amount === 'number' && amount > 0 ? amount.toString() : '';
                      })()}
                      required
                      postIcon={
                        selectedBillingInterval ? (
                          <span className="text-gray-800">/ {findSelectedOptionName(selectedBillingInterval)}</span>
                        ) : undefined
                      }
                    />
                  )}
                />
              ) : (
                <Input
                  type="number"
                  {...register(`recurringFees.${index}.amount`, { valueAsNumber: true })}
                  placeholder={t('enrollments.amount')}
                  label={t('enrollments.amount')}
                  className="h-9 w-full rounded-md border"
                  value={watch(`recurringFees.${index}.amount`)?.toString()}
                />
              )}
              {errors.recurringFees?.[index]?.amount && (
                <p className="text-sm text-red-950">{errors.recurringFees[index]?.amount?.message}</p>
              )}
            </div>
            <div className="col-span-3 flex items-end">
              <Button
                primary={isCurrency(index)}
                onClick={() => setValue(`recurringFees.${index}.amountType`, DiscountAmountType.CURRENCY)}
                extraClasses="h-9 w-9"
                disabled={watch(`recurringFees.${index}.type`) === 'Fee'}
              >
                $
              </Button>
              {watch(`recurringFees.${index}.type`) !== 'Fee' && (
                <Button
                  primary={!isCurrency(index)}
                  onClick={() => setValue(`recurringFees.${index}.amountType`, DiscountAmountType.PERCENT)}
                  extraClasses="h-9 w-9"
                  disabled={watch(`recurringFees.${index}.type`) === 'Fee'}
                >
                  %
                </Button>
              )}
            </div>
            <div className="col-span-1 ml-4 flex items-end pb-2">
              <SubtractIcon onClick={() => removeRecurringFee(index)} className="size-6 cursor-pointer text-blue-900" />
            </div>
          </div>
        ))}
      </div>

      {recurringInvoiceTotal > 0 && nextInvoices.length > 0 && (
        <InvoicePreview
          header={
            <>
              {t('enrollments.invoicesPreviewTitle')}{' '}
              {calculatedProrateAmount > 0 ? (
                <>
                  <span>{t('enrollments.invoicesPreviewTitleProrated')} </span>
                  <span className="font-bold">{dayjs(startDate).format('MMMM D, YYYY')}.</span>
                </>
              ) : (
                <>
                  <span>{t('enrollments.invoicesPreviewTitleNoProrated')} </span>
                  <span className="font-bold">{dayjs(startDate).format('MMMM D, YYYY')}.</span>
                </>
              )}
            </>
          }
          nextInvoices={nextInvoices}
          onEdit={calculatedProrateAmount > 0 ? () => setIsModalOpen(true) : undefined}
        />
      )}

      <Divider />
    </>
  );
};

export default RecurringFeesForm;
